/**
 * VRDeviceHandler - Abstract base class for VR device-specific logic
 *
 * This provides a clean abstraction for handling controller vs hand-specific functionality
 * without any branching logic in the concrete implementations.
 */

import type { World } from '../game/World.js';
import type * as THREE from 'three';


export abstract class VRDeviceHandler {
  protected inputSource: XRInputSource;
  protected renderer: THREE.WebGLRenderer;
  protected world: World;
  protected controllerIndex: number;

  constructor(
    inputSource: XRInputSource,
    renderer: THREE.WebGLRenderer,
    world: World,
    controllerIndex: number
  ) {
    this.inputSource = inputSource;
    this.renderer = renderer;
    this.world = world;
    this.controllerIndex = controllerIndex;
  }

  /** Get the input source */
  public getInputSource(): XRInputSource {
    return this.inputSource;
  }

  /** Get handedness of this device */
  public getHandedness(): 'left' | 'right' | 'none' {
    return this.inputSource.handedness as 'left' | 'right' | 'none';
  }

  /** Get controller index */
  public getControllerIndex(): number {
    return this.controllerIndex;
  }

  /** Get the Three.js target ray group (for pointing/raycasting) */
  public abstract getTargetRayGroup(): THREE.Group | null;

  /** Get the Three.js grip group (for object attachment) */
  public abstract getGripGroup(): THREE.Group | null;

  /** Create and setup the visual model for this device */
  public abstract createVisualModel(): void;

  /** Clean up resources and dispose of visual models */
  public abstract dispose(): void;

  /** Per-frame update hook. Default no-op. */
  public update(_deltaTime?: number): void { /* no-op by default */ }
}
